import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import { useMemo } from 'react';
import type { CognitoUser } from '../../../types';

export const createPortalUserManagementService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    listPortalUsers: async (customerId: string): Promise<CognitoUser[]> => {
      return rpcCall(client, 'admin.ListPortalUsers', { customer_id: customerId });
    },

    invitePortalUser: async (email: string, name: string, customerId: string): Promise<{ username: string; email: string; status: string }> => {
      return rpcCall(client, 'admin.InvitePortalUser', { email, name, customer_id: customerId });
    },

    resendPortalInvite: async (email: string, name: string, customerId: string): Promise<void> => {
      return rpcCall<void>(client, 'admin.ResendPortalInvite', { email, name, customer_id: customerId });
    },

    deletePortalUser: async (username: string, customerId: string): Promise<void> => {
      return rpcCall<void>(client, 'admin.DeletePortalUser', { username, customer_id: customerId });
    },

    resetPortalUserPassword: async (username: string, customerId: string): Promise<void> => {
      return rpcCall<void>(client, 'admin.ResetPortalUserPassword', { username, customer_id: customerId });
    },

    updatePortalUser: async (
      customerId: string,
      username: string,
      update: { name?: string; phone_number?: string },
    ): Promise<void> => {
      return rpcCall<void>(client, 'customers.UpdatePortalUser', {
        customer_id: customerId,
        username,
        ...update,
      });
    },
  };
};

export const usePortalUserManagementService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    return createPortalUserManagementService(idToken);
  }, [idToken]);
};
